import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import _get from 'lodash/get';
import _noop from 'lodash/noop';
import _isEqual from 'lodash/isEqual';
import { FormProvider, useForm } from 'react-hook-form';
import { useBlocker } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { Values } from '@lib/interfaces/form';
import PageSteps from '@lib/layouts/DashboardLayout/PageSteps/PageSteps';
import { Steps } from '@lib/enums/form';
import useStepParam from '@lib/hooks/useStepParam';
import Portal from '@lib/components/Portal/Portal';
import usePrevious from '@lib/hooks/usePrevious';
import replaceEmptyStringWithNull from '@lib/utils/replaceEmptyStringWithNull';
import { pageStepsId } from '@lib/enums/selectors';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import isDeeplyEmpty from '@lib/utils/isDeeplyEmpty';
import useFormPersistValues from '@lib/components/ReactHookForm/useFormPersistValues';
import isEqualWithSkipKeys from '@lib/utils/isEqualWithSkipKeys';
import { IconName } from '@lib/components/Modal/enums';
import { FormContainerProps } from './types';
import getFormData from './getFormData';
import getErrorsByStep from './getErrorsByStep';
import useHandleGraphQLErrors from './useHandleGraphQLErrors';
import FormProviderCustom from './FormProvider';
import Form from './Form';

export type { FormRef, ActionsProps } from './types';

const DIRTY_SKIP_KEYS = ['avatarAttached'];

/**
 * Entrypoint component that wires together React Hook Form with extra behaviours such as
 * optimistic persistence, navigation blocking and custom dirty tracking.
 */
function FormContainer(props: FormContainerProps) {
  const { t } = useTranslation();
  const {
    actions,
    actionsComponent,
    actionsComponentProps,
    defaultValues,
    disableAwayConfirmation = false,
    fields,
    formClassName,
    formId = 'form',
    formItemsClassName,
    formRef,
    graphQLErrors,
    graphQLErrorsReplacePathMap,
    graphQLErrorsSkipPath,
    loading = false,
    loadingDesc = t('processing-is-ongoing'),
    onSubmit,
    persistForm = false,
    persistFormSeconds = 0,
    renderAfterForm,
    sourceAppendValues,
    sourceFields,
    steps,
    stepsCount,
    submitOnChange = false,
    reValidateMode = 'onBlur',
  } = props;
  const prevDefaultValues = usePrevious(defaultValues);
  const [stepParam] = useStepParam();
  const [isAwayModalOpen, setAwayModalOpen] = useState(false);

  const stepIndex =
    steps && steps.length > 1 && stepParam ? Number(stepParam) : Steps.step1;

  const { setPersistedValues, getPersistedValues } = useFormPersistValues({
    formId,
    persistForm,
    persistFormSeconds,
  });

  const persistedValues = getPersistedValues();

  const { fieldsArray, schemaObj, fieldChangeCallBacks } = useMemo(
    () =>
      getFormData({
        fields,
        formId,
        formItemsClassName,
        stepIndex,
        t,
        sourceAppendValues,
      }),
    [fields, formId, formItemsClassName, sourceAppendValues, stepIndex, t],
  );
  const formDefaultValues = useMemo(
    () => ({ ...defaultValues, ...persistedValues }),
    [defaultValues, persistedValues],
  );
  const methods = useForm({
    // @ts-ignore
    defaultValues: formDefaultValues,
    mode: 'all',
    reValidateMode,
    // Collect all validation errors, not just the first
    // Note: yupResolver already defaults to abortEarly: false; passing as 2nd arg to satisfy types
    resolver: yupResolver(Yup.object(schemaObj), { abortEarly: false }),
    shouldFocusError: true,
  });
  const {
    handleSubmit,
    formState,
    reset: rhfReset,
    setError,
    setValue,
    watch,
    getValues,
    trigger,
  } = methods;
  const skipCallbacksRef = useRef(false);
  /**
   * Creates a deep copy of the current form values to decouple baseline snapshots
   * from RHF's internal mutable state.
   */
  const cloneValues = useCallback(
    (source?: Values) => {
      const snapshot = source ?? getValues();
      try {
        if (typeof structuredClone === 'function') {
          return structuredClone(snapshot);
        }
      } catch (error) {
        console.warn(
          '[FormContainer] structuredClone failed, falling back to JSON clone.',
          error,
        );
      }
      try {
        return JSON.parse(JSON.stringify(snapshot));
      } catch (error) {
        console.warn(
          '[FormContainer] JSON clone failed, returning snapshot by reference.',
          error,
        );
        return snapshot;
      }
    },
    [getValues],
  );
  /**
   * Wrapper around RHF setValue that keeps dirty tracking consistent during
   * hydration and user-driven updates.
   */
  const setValueWithDirty = useCallback<typeof setValue>(
    (name, value, options) => {
      const shouldDirty = options?.shouldDirty ?? !skipCallbacksRef.current;
      return setValue(name, value, {
        ...options,
        shouldDirty,
      });
    },
    [setValue],
  );
  methods.setValue = setValueWithDirty as typeof methods.setValue;
  const unmuteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const baselineValuesRef = useRef<Values>(cloneValues());
  const hasLocalChangesRef = useRef(false);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  /**
   * Determines whether new form values diverge from the baseline snapshot.
   */
  const evaluateChanges = useCallback(
    (nextValues: Values, fieldName?: string) => {
      const baseline = baselineValuesRef.current;
      if (!fieldName) {
        return !isEqualWithSkipKeys(nextValues, baseline, DIRTY_SKIP_KEYS);
      }
      const baselineValue = _get(baseline, fieldName);
      const currentValue = _get(nextValues, fieldName);
      if (!_isEqual(baselineValue, currentValue)) {
        return true;
      }
      if (hasLocalChangesRef.current) {
        return !isEqualWithSkipKeys(nextValues, baseline, DIRTY_SKIP_KEYS);
      }
      return false;
    },
    [],
  );

  const refreshBaseline = useCallback(() => {
    baselineValuesRef.current = cloneValues();
    hasLocalChangesRef.current = false;
    setHasLocalChanges(false);
  }, [cloneValues]);
  const clearPendingUnmute = useCallback(() => {
    if (unmuteTimeoutRef.current) {
      clearTimeout(unmuteTimeoutRef.current);
      unmuteTimeoutRef.current = null;
    }
  }, []);
  /**
   * Re-enables change callbacks on the next tick and captures a fresh baseline.
   */
  const scheduleUnmute = useCallback(() => {
    clearPendingUnmute();
    unmuteTimeoutRef.current = setTimeout(() => {
      skipCallbacksRef.current = false;
      unmuteTimeoutRef.current = null;
      refreshBaseline();
    }, 0);
  }, [clearPendingUnmute, refreshBaseline]);
  const resetForm = useCallback(
    (
      resetValues?: Parameters<typeof rhfReset>[0],
      resetOptions?: Parameters<typeof rhfReset>[1],
    ) => {
      skipCallbacksRef.current = true;
      rhfReset(resetValues, resetOptions);
      scheduleUnmute();
    },
    [rhfReset, scheduleUnmute],
  );
  methods.reset = resetForm as typeof methods.reset;
  const { errors, isSubmitSuccessful } = formState;
  const values = getValues();

  const isStepsVisible = !!stepsCount && !!steps?.length;

  const {
    proceed = _noop,
    state,
    reset: blockerReset,
  } = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !disableAwayConfirmation &&
      hasLocalChanges &&
      !isDeeplyEmpty(values) &&
      !_isEqual(currentLocation.pathname, nextLocation.pathname),
  );

  const onSubmitHandler = useCallback(
    (newValues: Values) => {
      if (onSubmit)
        onSubmit(replaceEmptyStringWithNull(newValues, ['locationGeojson']));
    },
    [onSubmit],
  );

  const onAwayConfirmed = () => {
    setAwayModalOpen(false);
    resetForm();
    proceed();
  };

  const onCloseAwayModal = () => {
    setAwayModalOpen(false);
    if (blockerReset) blockerReset();
  };

  if (formRef) {
    formRef.current = {
      reset: resetForm,
      errors,
      setValue: setValueWithDirty,
      values,
      isDirty: hasLocalChanges,
    };
  }

  useEffect(() => {
    if (
      defaultValues &&
      prevDefaultValues &&
      !isEqualWithSkipKeys(defaultValues, prevDefaultValues, ['avatarAttached'])
    ) {
      resetForm({
        ...values,
        ...defaultValues,
      });
      refreshBaseline();
    }
  }, [defaultValues, values, prevDefaultValues, refreshBaseline, resetForm]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      resetForm(cloneValues(values), {
        keepDirty: false,
        keepDirtyValues: false,
      });
      refreshBaseline();
    }
  }, [cloneValues, isSubmitSuccessful, refreshBaseline, resetForm, values]);

  useEffect(() => {
    const subscription = watch((newValues, { name, type }) => {
      if (submitOnChange && type === 'change') {
        handleSubmit(onSubmitHandler)();
      }
      // @ts-ignore
      setPersistedValues(newValues);
      if (skipCallbacksRef.current) {
        return;
      }
      const nextHasChanges = evaluateChanges(
        newValues as Values,
        name as string | undefined,
      );
      if (nextHasChanges !== hasLocalChangesRef.current) {
        hasLocalChangesRef.current = nextHasChanges;
        setHasLocalChanges(nextHasChanges);
      }
      // Use Set to avoid duplicate callback executions
      const uniqueCallbacks = [...new Set(fieldChangeCallBacks)];
      uniqueCallbacks.forEach((fieldChangeCallBack) => {
        if (fieldChangeCallBack) {
          fieldChangeCallBack({
            values: newValues as Values,
            type: type as string,
            setValue: setValueWithDirty,
            trigger,
            name,
          });
        }
      });
    });
    return () => subscription.unsubscribe();
  }, [
    fieldChangeCallBacks,
    handleSubmit,
    onSubmitHandler,
    setPersistedValues,
    submitOnChange,
    trigger,
    watch,
    setValueWithDirty,
    evaluateChanges,
  ]);

  useEffect(() => {
    if (state === 'blocked') {
      setAwayModalOpen(true);
    }
  }, [state]);

  useEffect(
    () => () => {
      if (blockerReset) blockerReset();
      clearPendingUnmute();
    },
    [blockerReset, clearPendingUnmute],
  );

  useHandleGraphQLErrors({
    graphQLErrors,
    graphQLErrorsSkipPath,
    graphQLErrorsReplacePathMap,
    setError,
  });

  const errorsByStep = getErrorsByStep(fields, errors);

  return (
    <>
      {isStepsVisible && (
        <Portal portalRootId={pageStepsId}>
          <PageSteps data={steps} errorsByStep={errorsByStep} />
        </Portal>
      )}
      <FormProvider {...methods}>
        <FormProviderCustom>
          <Form
            actions={actions}
            actionsComponent={actionsComponent}
            actionsComponentProps={actionsComponentProps}
            fieldsArray={fieldsArray}
            formClassName={formClassName}
            formItemsClassName={formItemsClassName}
            formState={formState}
            handleSubmit={handleSubmit}
            loading={loading}
            loadingDesc={loadingDesc}
            isDirtyOverride={hasLocalChanges}
            onSubmitHandler={onSubmitHandler}
            renderAfterForm={renderAfterForm}
            sourceFields={sourceFields}
            stepsCount={stepsCount}
          />
        </FormProviderCustom>
      </FormProvider>
      <ConfirmationModal
        confirmButtonLabel={t('leave-without-saving')}
        description={t('changes-are-not-saved-desc')}
        icon={IconName.Warning}
        isOpen={isAwayModalOpen}
        onClose={onCloseAwayModal}
        onConfirm={onAwayConfirmed}
        title={t('changes-are-not-saved')}
      />
    </>
  );
}

export default memo(FormContainer);
